import React from 'react';
import {
  Box, Button, Stack, Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import { Trans } from '@gamiphy/react-i18n';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import { fullMonthFormatDate } from '@gamiphy/utils/date';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import cardCoin from '@gamiphy/loyalty-station-desktop/components/cashback/assets/card-coin.svg';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { formatCashbackPromotionValue, formatDiscountValue } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';
import { DiscountDto, DiscountDtoSourceEnum } from '@coretava/api/ecommerce-service';

const DiscountDetails: React.FC<{ discount: DiscountDto }> = ({ discount }) => {
  const text = React.useMemo(() => {
    if (!discount.cashbackConfig.multiplier) {
      return (
        <Trans
          i18nKey="coreloyalty.cashback.autoApply.zeroMultiplier"
          defaults="It'll be applied automatically when you make your next purchase!"
          values={{
            minRedemptionPurchase: discount.cashbackConfig.minRedemptionValue,
          }}
        />
      );
    }
    if (discount.cashbackConfig.partialRedemptionEnabled) {
      return (
        <Trans
          i18nKey="coreloyalty.cashback.autoApply.partialRedemption"
          defaults="It'll be applied automatically to your next purchase capped at a maximum of {{multiplierPercentage}}% of the next purchase value"
          values={{
            multiplierPercentage: discount.cashbackConfig.multiplier,
          }}
        />
      );
    }
    if (!discount.cashbackConfig.partialRedemptionEnabled) {
      return (
        <Trans
          i18nKey="coreloyalty.cashback.autoApply"
          defaults="It'll be applied automatically when you make your next purchase over {{minRedemptionPurchase, currency}}"
          values={{
            minRedemptionPurchase: discount.cashbackConfig.minRedemptionValue,
          }}
        />
      );
    }
    return undefined;
  }, [discount]);

  return (
    <Stack spacing={1}>
      {AppCashbackStore.currentAppCashback?.enabled && (
        <Box display="flex" alignItems="center" gap={1} p={0.75} bgcolor="#FFF6EE" borderRadius="12px">
          <Box component="img" src={cardCoin} />
          <Typography fontSize={14} fontWeight={500}>
            {
            AppStore.app.agent === 'wake' ? (
              <Trans
                i18nKey="coreloyalty.cashback.allPurchases.predefinedPromotions"
                defaults="Get up to {{promotionValue}} cashback on all your purchases!"
                values={{
                  promotionValue: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
                }}
              />
            ) : (
              <Trans
                i18nKey="coreloyalty.cashback.allPurchases"
                defaults="Get {{cashbackPercentage}}% cashback on all your purchases!"
                values={{
                  cashbackPercentage: AppCashbackStore.currentAppCashback?.percentage,
                }}
              />
            )
          }
          </Typography>
        </Box>
      )}
      <Typography fontSize={28} fontWeight={700} color="#22c87c">
        {formatDiscountValue(discount)}
      </Typography>
      <Typography fontSize={14} fontWeight={600} textTransform="uppercase">
        {text}
      </Typography>
      {
        discount.source !== DiscountDtoSourceEnum.WelcomeGift && (
          <Typography fontSize={12} fontWeight={500} sx={{ opacity: 0.7 }}>
            •
            {' '}
            <Trans
              i18nKey="coreloyalty.cashback.discount.validity"
              defaults="Valid until {{validUntilDate}}. Starting from {{validFromDate}}"
              values={{
                validUntilDate: discount.expiresAt && fullMonthFormatDate(discount.expiresAt),
                validFromDate: fullMonthFormatDate(discount.validFrom),
              }}
            />
          </Typography>
        )
      }
      <Button variant="contained" color="primary" onClick={() => UiStore.close()}>
        <Typography fontSize={16}>
          <Trans
            i18nKey="coreloyalty.cashback.shopNow"
            defaults="Shop now"
          />
        </Typography>
      </Button>
    </Stack>
  );
};

export default observer(DiscountDetails);
